import React from 'react';
import { useNavigate } from 'react-router-dom';
import ServicesSection from './ServicesSection';
import ValuePropSection from './ValuePropSection';
import Footer from './Footer';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { useTheme } from '@/context/ThemeContext';
import { GridScan } from './GridScan';
import { Typewriter } from './ui/Typewriter';

interface LandingPageProps {
  user: any | null;
}

const LandingPage: React.FC<LandingPageProps> = () => {
  const navigate = useNavigate();
  const { resolved } = useTheme();
  const isLight = resolved === 'light';
  const badge = useScrollReveal();
  const heading = useScrollReveal();
  const subtitle = useScrollReveal();
  const ctas = useScrollReveal();

  return (
    <div className="min-h-screen bg-background text-foreground overflow-x-hidden">
      {/* Hero Section */}
      <section className="min-h-[calc(100vh-2.5rem)] flex items-center justify-center pt-20 pb-8 md:min-h-[calc(100vh-3rem)] md:pt-24 md:pb-10">
        <div className="max-w-6xl w-full mx-auto px-4 md:px-6">
          {/* Hero Grid Card */}
          <div className="relative rounded-none border border-overlay/[0.08] bg-overlay/[0.01] overflow-hidden min-h-[580px] md:min-h-[625px] flex flex-col">
            {/* Grid Scan Background */}
            <div className="absolute inset-0 z-0 overflow-hidden pointer-events-none opacity-[0.25]">
              <div className="absolute inset-0 pointer-events-none">
                <GridScan
                  sensitivity={0.55}
                  lineThickness={1}
                  linesColor={isLight ? "#D1D5DB" : "#ffffff"}
                  scanColor={isLight ? "#9CA3AF" : "#fffafa"}
                  scanOpacity={isLight ? 0.15 : 0.35}
                  gridScale={0.2}
                  lineStyle="solid"
                  lineJitter={0}
                  scanDirection="pingpong"
                  noiseIntensity={0}
                  chromaticAberration={0}
                  scanGlow={0.3}
                  scanSoftness={2}
                  scanDuration={2.5}
                  scanDelay={1.5}
                  scanOnClick={false}
                />
              </div>
            </div>

            {/* Top horizontal line */}
            <div className="absolute top-12 left-0 w-full h-px bg-overlay/[0.04] pointer-events-none z-10" />

            {/* Corner markers */}
            <span className="absolute top-3 left-4 text-overlay/[0.15] text-sm font-mono select-none z-20">+</span>
            <span className="absolute bottom-3 right-4 text-overlay/[0.15] text-sm font-mono select-none z-20">+</span>

            {/* Content area */}
            <div className="relative z-10 flex-1 flex flex-col items-center justify-center text-center px-6 md:px-8 pt-14 md:pt-16 pb-5">
              <div
                ref={badge.ref}
                className={`inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full border border-overlay/[0.08] bg-overlay/[0.03] backdrop-blur-sm mb-7 transition-all duration-600 ${badge.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
              >
                <span className="text-[13px] font-medium text-muted-foreground">Enterprise AI Agent Platform</span>
              </div>

              <h1
                ref={heading.ref}
                className={`text-[2.0rem] sm:text-[2.5rem] md:text-[3.3rem] lg:text-[4.0rem] font-bold mb-6 tracking-[-0.05em] leading-[0.9] transition-all duration-700 ${heading.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                style={{ animationDelay: '100ms' }}
              >
                Build your {' '}
                <span className={`inline-flex align-middle mr-1 text-[0.9em] ${isLight ? 'text-foreground' : 'text-red-400'}`}>
                  
                </span>{' '}
                <span className="text-primary">
                  <Typewriter
                    key={heading.isVisible ? 'visible' : 'hidden'}
                    text={["AI Workforce", "Automated Operations", "Enterprise Intelligence"]}
                    speed={100}
                    initialDelay={500}
                    showCursor={true}
                    loop={true}
                    waitTime={2000}
                    deleteSpeed={50}
                  />
                </span>
              </h1>

              <p
                ref={subtitle.ref}
                className={`text-[15px] md:text-[18px] text-muted-foreground font-light max-w-[44rem] mx-auto leading-relaxed transition-all duration-600 ${subtitle.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                style={{ animationDelay: '200ms' }}
              >
                We transform traditional businesses into autonomous enterprises using advanced AI Agents. Scale operations and unlock unprecedented efficiency.
              </p>

              <div
                ref={ctas.ref}
                className={`mt-10 flex flex-col md:flex-row gap-3.5 justify-center transition-all duration-600 ${ctas.isVisible ? 'animate-reveal-up' : 'opacity-0'}`}
                style={{ animationDelay: '300ms' }}
              >
                <button
                  onClick={() => navigate('/contact-us')}
                  className="inline-flex items-center justify-center gap-2.5 bg-foreground text-background px-6 md:px-7 py-3 rounded-full font-medium text-[15px] hover:opacity-90 transition-colors"
                >
                  <img
                    src={isLight ? "/Antz-Single-Element-Lightmode.png" : "/Antz-Single-Element-Darkmode.png"}
                    alt=""
                    aria-hidden="true"
                    className="w-6 h-6 object-cover scale-125"
                  />
                  Start Transformation
                </button>
                <button
                  onClick={() => navigate('/products')}
                  className="bg-overlay/[0.02] text-foreground px-6 md:px-7 py-3 rounded-full font-medium text-[15px] hover:bg-overlay/[0.06] transition-colors border border-overlay/[0.12] backdrop-blur-sm"
                >
                  See What We Build
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <ValuePropSection />
      <ServicesSection />
      <Footer />
    </div>
  );
};

export default LandingPage;
